import { createDefaultSectionLoggerFactory } from "Logging/defaultLogger";
import { getDefaultLoggingSettings, LoggingSection } from "Logging/index";
import { TypeIdentifier } from "./DIContainerInterfaces";
import { InvalidArgumentException, InvalidOperationException } from "./Exceptions";
import { GlobalContext } from "./GlobalContext";

/**
 * Dekorátor, který jde použít pro injectování parametru
 * @includeToDoc
 */
export type ParameterDecorator = (target: Object, propertyKey: string | symbol, parameterIndex: number) => void;

/**
 * Rozhraní Dependency Injection kontejneru
 * @includeToDoc
 */
export interface ITypeFactory {
    /**
     * Získá instanci konkrétní implementace pro rozhraní definované identifikátorem (pro singletony je vracena vždy jedna instance)
     * @param typeIdentifier Identifikátor definující, pro které rozhraní se má hledat implementace
     * @param params Volitelné parametry, které se předávají do konstruktoru vytvářené property
     * @returns Instance třídy implementující rozhraní T
     */
    create<T>(typeIdentifier: TypeIdentifier<T>, ...params: any[]): T;

    /**
     * Vrátí příznak, zda je dostupná instance konkrétní implementace pro rozhraní definované identifikátorem
     * @param typeIdentifier Identifikátor definující hledanou implementaci se zadaným rozhraním
     * @returns Příznak, zda je dostupná instance třídy implementující rozhraní T
     */
    has<T>(typeIdentifier: TypeIdentifier<T>): boolean;

    /**
     * Vytvoří tovární funkci vytvářející instanci konkrétní implementace pro rozhraní definované identifikátorem (pro singletony je vracena vždy jedna instance)
     * @param typeIdentifier Identifikátor definující, pro které rozhraní se má hledat implementace
     * @param params Volitelné parametry, které se předávají do konstruktoru vytvářené property
     * @returns Tovární funkci vytvářející instanci třídy implementující rozhraní T
     */
    createFactory<T>(typeIdentifier: TypeIdentifier<T>, ...params: any[]): () => T;

    /**
     * Zaregistruje implementaci pro rozhraní definované identifikátorem
     * @param typeIdentifier Identifikátor definující rozhraní
     * @param classImplementedType Třída implementující dané rozhraní
     * @param isSingleton Zda je třída singleton, ted zda se má vždy vracet stejná instance
     * @param params Volitelné parametry předávané do konstruktoru při vytváření instance
     */
    register<T>(typeIdentifier: TypeIdentifier<T>, classImplementedType: new(...params: any[]) => T, isSingleton?: boolean, ...params: any[]): void;

    /**
     * Zaregistruje tovární funkci vracející implementaci pro rozhraní definované identifikátorem
     * @param typeIdentifier Identifikátor definující rozhraní
     * @param factoryFunction Tovární funkce vracející implementaci pro rozhraní definované identifikátorem
     * @param isSingleton Zda je třída singleton, ted zda se má vždy vracet stejná instance
     * @returns {}
     */
    registerFactory<T>(typeIdentifier: TypeIdentifier<T>, factoryFunction?: (...params: any[]) => T, isSingleton?: boolean): void;

    /**
     * Dekorátor pro automatické předání instance z TypeFactory do zadaného parametru
     * @param typeIdentifier Identifikátor definující rozhraní
     */
    bindType<T>(typeIdentifier: TypeIdentifier<T>): ParameterDecorator;

    /**
     * Dekorátor pro automatické předání funkce, která vrací instance z TypeFactory
     * @param typeIdentifier Identifikátor definující rozhraní
     */
    bindFactory<T>(typeIdentifier: TypeIdentifier<T>): ParameterDecorator;
}

// -------------------------------------------------------- defaultní implementace ---------------------------------------------------------

interface IFactoryData {
    classImplementedType?: new(...params: any[]) => any;
    factoryFunction?: (...params: any[]) => any;
    isSingleton?: boolean;
    singletonInstance?: any;
    params?: any[];
}

const _typeFactoryMetadata: string | symbol = (self as any).Symbol ? Symbol("typeFactoryMetadata") : "__TSW_TypeFactory_Metadata__";

interface ITypeFactoryMetadata<T> {
    parameterBinds: Array<{ type: ITypeFactoryBindType; typeIdentifier: TypeIdentifier<T>; params: any[] }>;
}

enum ITypeFactoryBindType { Type, Factory }

/** Nastaveni urcuje zda v konzoli vypsat seznam properties/methods, ktere nebyly nalezeny v interface */
const DEBUG_TYPE_FACTORY_SHOW_NOT_FOUND_ITEMS = false;

/**
 * Defaultní implementace ITypeFactory
 */
class _TypeFactory implements ITypeFactory {
    //  ----- zvláštní syntaxe kvůli ES5 ;-)  ----------------------------
    private _registeredTypes: { [key: string]: IFactoryData } = {};

    private _hasInstanceIsDefined = (self as any).Symbol && (self as any).Symbol.hasInstance;

    private readonly _logger = createDefaultSectionLoggerFactory(getDefaultLoggingSettings(false)).create(LoggingSection.dependencyInjection);

    private _checkInterfaceSupport(typeIdentifier: TypeIdentifier<any>, obj: any): void {
        if (typeof typeIdentifier === "function" &&
            this._hasInstanceIsDefined &&
            (typeIdentifier as any)[(self as any).Symbol.hasInstance] &&
            !(typeIdentifier as any)[(self as any).Symbol.hasInstance](obj)) {
            if (DEBUG_TYPE_FACTORY_SHOW_NOT_FOUND_ITEMS) {
                // debug
                try {
                    //_properties.some((value) => !Reflect.has(instance, value)
                    const notFoundProp = [];
                    const notFoundFnc = [];
                    for (const prop of (typeIdentifier as any)._properties) {
                        if (!Reflect.has(obj, prop)) notFoundProp.push(prop);
                    }
                    for (const prop of (typeIdentifier as any)._methods) {
                        if (typeof obj[prop] !== "function") {
                            notFoundFnc.push(prop);
                        }
                    }

                    this._logger.log("Not found props: ", notFoundProp);
                    this._logger.log("Not found fnc: ", notFoundFnc);
                } catch (e) { /* ignore */ }
            }
            throw new InvalidOperationException(`Class ${obj.constructor.name} does not implement interface ${this._getIdentifierName(typeIdentifier)}!`); //`
        }
    }
    //  ----- zvláštní syntaxe kvůli ES5 ;-)  ----------------------------

    private _getIdentifierName(typeIdentifier: TypeIdentifier<any>): string {
        switch (typeof typeIdentifier) {
            case "function":
                return (typeIdentifier as any).name; // pokud bych to napsal do jako if, tak by tam nemuselo být přetypování na any, ale tohle TypeScript neumí
            case "string":
                return typeIdentifier;
            case "symbol":
                return /Symbol\((\w*)\)/.exec((typeIdentifier as any).toString()) ![1];
        }
        throw new InvalidArgumentException("typeIdentifier");
    }

    public create<T>(typeIdentifier: TypeIdentifier<T>, ...params: any[]): T {
        const identifierName = this._getIdentifierName(typeIdentifier);

        const typeDef = this._registeredTypes[identifierName];
        if (!typeDef)
            throw new InvalidOperationException(`Implementation for ${typeof typeIdentifier}(${identifierName}) not found!`); //`

        if (typeDef.isSingleton) {
            if (!typeDef.singletonInstance)
                typeDef.singletonInstance = this._createInstance(typeIdentifier, typeDef, ...params);
            return typeDef.singletonInstance as T;
        }

        return this._createInstance(typeIdentifier, typeDef, ...params) as T;
    }

    public has<T>(typeIdentifier: TypeIdentifier<T>): boolean {
        const identifierName = this._getIdentifierName(typeIdentifier);
        return !!this._registeredTypes[identifierName];
    }

    public createFactory<T>(typeIdentifier: TypeIdentifier<T>, ...params: any[]): (...params: any[]) => T {
        const getParams = (params: any[], params2: any[]) => (params ? params.concat(params2) : params2);
        return (...params2) => this.create(typeIdentifier, ...getParams(params, params2));
    }

    public register<T>(typeIdentifier: TypeIdentifier<T>, classImplementedType: new() => T, isSingleton?: boolean, ...params: any[]): void {
        this._checkInterfaceSupport(typeIdentifier, classImplementedType.prototype);

        const identifierName = this._getIdentifierName(typeIdentifier);
        this._registeredTypes[identifierName] = { classImplementedType: classImplementedType, isSingleton: !!isSingleton, params: params };
    }

    public registerFactory<T>(typeIdentifier: TypeIdentifier<T>, factoryFunction: (...params: any[]) => T, isSingleton?: boolean): void {
        const identifierName = this._getIdentifierName(typeIdentifier);
        this._registeredTypes[identifierName] = { factoryFunction: factoryFunction, isSingleton: !!isSingleton };
    }

    private _createInstance(typeIdentifier: TypeIdentifier<any>, typeDef: IFactoryData, ...params: any[]): any {
        const getParams = () => (typeDef.params ? typeDef.params.concat(params) : params);

        let result: any;

        if (typeDef.classImplementedType) {
            const impType = typeDef.classImplementedType;
            const metadata: ITypeFactoryMetadata<any> = (impType as any)[_typeFactoryMetadata as any];
            let _params: Array<any>;
            if (metadata && metadata.parameterBinds) {
                _params = [];
                // naplní hodnoty podle dekorátorů
                for (const index in metadata.parameterBinds) {
                    const pBinds = metadata.parameterBinds[index];
                    switch (pBinds.type) {
                        case ITypeFactoryBindType.Type:
                            _params[index] = this.create(pBinds.typeIdentifier, ...pBinds.params);
                            break;
                        case ITypeFactoryBindType.Factory:
                            _params[index] = this.createFactory(pBinds.typeIdentifier, ...pBinds.params);
                            break;
                    }
                }
                let pIndex = 0;
                // hodnoty nenaplněné dokorátory doplní hodnotami z parametrů
                for (const param of getParams()) {
                    while (_params[pIndex])
                        pIndex++;
                    _params[pIndex] = param;
                }
            } else
                _params = getParams();

            result = new typeDef.classImplementedType(..._params);
        }

        if (typeDef.factoryFunction)
            result = typeDef.factoryFunction(...getParams());

        if (result) {
            this._checkInterfaceSupport(typeIdentifier, result);
            return result;
        }

        throw new InvalidOperationException(`Implementation for ${typeof typeIdentifier}(${this._getIdentifierName(typeIdentifier)}) not found!`); //`
    }

    public bindType<T>(typeIdentifier: TypeIdentifier<T>, ...params: any[]): ParameterDecorator {
        return (target: Object, _propertyKey: string | symbol, parameterIndex: number) => {
            if (typeof target !== "function")
                throw new InvalidArgumentException("target");
            this._createMetadataItem(target, parameterIndex, typeIdentifier, params, ITypeFactoryBindType.Type);
        };
    }

    public bindFactory<T>(typeIdentifier: TypeIdentifier<T>, ...params: any[]): ParameterDecorator {
        return (target: Object, _propertyKey: string | symbol, parameterIndex: number) => {
            if (typeof target !== "function")
                throw new InvalidArgumentException("target");
            this._createMetadataItem(target, parameterIndex, typeIdentifier, params, ITypeFactoryBindType.Factory);
        };
    }

    private _createMetadataItem<T>(target: Object, parameterIndex: number, typeIdentifier: TypeIdentifier<T>, params: any[], type: ITypeFactoryBindType): void {
        const metadata: ITypeFactoryMetadata<T> = (target as any)[_typeFactoryMetadata as any] || ((target as any)[_typeFactoryMetadata as any] = {});
        const parameterBinds = metadata.parameterBinds || (metadata.parameterBinds = []);
        parameterBinds[parameterIndex] = {
            type: type,
            typeIdentifier: typeIdentifier,
            params: params,
        };
    }
}

// -------------------------------------------------------------------------------------------------------------------------------------------

/**
 * Pomocná třída pro vytváření lazy getteru property pro instancializaci z DI kontejneru
 * @includeToDoc
 */
export class InjectedProperty {
    /**
     * Vytvoří property s lazy getterem, který vrací singleton instanci vytvořenou DI kontejnerem
     * @param object Objekt, na kterém se má property vytvořit
     * @param propertyName Název property
     * @param typeIdentifier Identifikátor, podle kterého se má najít konkrétní implementace v kontejneru
     */
    public static bindProperty(object: any, propertyName: PropertyKey, typeIdentifier: TypeIdentifier<any>): void {
        let instance: any;
        Object.defineProperty(object, propertyName, {
            get: () => {
                if (!instance)
                    instance = DIContainer.instance.create(typeIdentifier);
                return instance;
            },
        });
    }
}

/**
 * @includeToDoc
 */
export class LazyProperty {
    /**
     * Vytvoří property s lazy getterem, který vrací instanci vytvořenou pomocí předané tovární metody
     * @param object Objekt, na kterém se má property vytvořit
     * @param propertyName Název property
     * @param factoryMethod Metoda pro získání instance
     */
    public static bindProperty<T>(object: any, propertyName: PropertyKey, factoryMethod: () => T): void {
        let instance: any;
        Object.defineProperty(object, propertyName, {
            get: () => {
                if (!instance)
                    instance = factoryMethod();
                return instance;
            },
        });
    }
}

let _typeFactoryImplementation: new() => ITypeFactory = _TypeFactory;

/**
 * Metoda sloužící pro registraci vlastní implementace TypeFactory
 * @param impl třída reprezentující vlastní implementaci TypeFactory
 * @includeToDoc
 */
export function setDIContainerImplementation(impl: new() => ITypeFactory) {
    _typeFactoryImplementation = impl;
}

// ----- property DIContainer -------------------
let _typeFactory: ITypeFactory;

export function getDIContainer(): ITypeFactory {
    if (!_typeFactory)
        _typeFactory = new _typeFactoryImplementation();
    return _typeFactory;
}

/**
 * Třída sloužící pro přístup k Dependency Injection kontejneru
 */
export class DIContainer {
    public static get instance(): ITypeFactory {
        return getDIContainer();
    }
}
// ----- property TypeFactory --------------------

GlobalContext.registerType(DIContainer);
